import Button from '@ingka/button';
import Checkbox from '@ingka/checkbox';
import activateIcon from '@ingka/ssr-icon/paths/clipboard-checkmark';
import inactivateIcon from '@ingka/ssr-icon/paths/clipboard-strikethrough';
import crossIcon from '@ingka/ssr-icon/paths/cross';
import { cloneDeep } from 'lodash';
import { useEffect, useState } from 'react';
import ReactDOM from 'react-dom';

import { IconButton } from '../../../../../shared/components/IconButton';
import { StyledTooltip } from '../../../../../shared/styles/components/integrations.styles';
import { Modal } from '../../../../../shared/styles/components/modal.styles';
import {
   Caption,
   HintText,
   InfoLabel,
   InfoRow,
   InfoValue,
   LinkButton,
   Panel,
   SectionLabel,
   TruncatedText,
} from '../../../../../shared/styles/components/primitives.styles';
import { tokens } from '../../../../../shared/styles/core/tokens';
import { theme } from '../../../../../shared/styles/core/theme';
import { parseNumericValue, thousandSeparator } from '../../../../../shared/utils/formatters.utils';
import type { DuplicateWithContext } from '../../../state/duplicate-parts-modal.state';
import { useDuplicatePartsModalState } from '../../../state/duplicate-parts-modal.state';
import { useMutationWithCacheUpdate } from '../../../state/table-data.state';
import {
   ArrowSeparator,
   ComparisonGrid,
   GroupCard,
   IntroHint,
   LinkGroup,
   PanelCornerAction,
   RelativePanel,
   SelectableValueRow,
   SpreadRow,
} from './DuplicatePartsModal.styles';

type TransferField = 'mass' | 'volume';
type Selections = Record<string, Set<TransferField>>;
type ChangeSummaryField = keyof Pick<
   DuplicateWithContext['oldPart'],
   | 'mass'
   | 'volume'
   | 'totalWeight'
   | 'dwpTotalWeight'
   | 'quantity'
   | 'density'
   | 'areaDensity'
   | 'recycledContent'
   | 'renewableContent'
   | 'compositionShare'
   | 'comment'
   | 'threeDModelNo'
   | 'bomValidFrom'
>;

const TRANSFER_FIELDS: TransferField[] = ['mass', 'volume'];
const CHANGE_SUMMARY_FIELDS: ChangeSummaryField[] = [
   'mass',
   'volume',
   'totalWeight',
   'dwpTotalWeight',
   'quantity',
   'density',
   'areaDensity',
   'recycledContent',
   'renewableContent',
   'compositionShare',
   'comment',
   'threeDModelNo',
   'bomValidFrom',
];

const CHANGE_SUMMARY_LABELS: Record<ChangeSummaryField, string> = {
   mass: 'Mass',
   volume: 'Volume',
   totalWeight: 'Total weight',
   dwpTotalWeight: 'DWP item weight',
   quantity: 'Quantity',
   density: 'Density',
   areaDensity: 'Area density',
   recycledContent: 'Recycled content',
   renewableContent: 'Renewable content',
   compositionShare: 'Composition share',
   comment: 'Comment',
   threeDModelNo: '3D model no',
   bomValidFrom: 'BOM valid from',
};

const formatValue = (value: string | number | undefined | null): string => {
   if (!value && value !== 0) {
      return '–';
   }
   return thousandSeparator(Math.round(parseNumericValue(value)));
};

const hasValue = (value: string | number | undefined | null): boolean => {
   if (value === '0' || value === 0) {
      return true;
   }
   return Boolean(value);
};

const isZeroValue = (value: string | number | undefined | null): boolean => value === '0' || value === 0;

const fieldLabel = (field: TransferField): string => field.charAt(0).toUpperCase() + field.slice(1);

const isTransferField = (field: ChangeSummaryField): field is TransferField => TRANSFER_FIELDS.includes(field as TransferField);

const getChangedFields = (group: DuplicateWithContext): ChangeSummaryField[] =>
   CHANGE_SUMMARY_FIELDS.filter((field) => group.oldPart[field] !== group.newPart[field]);

const formatSummaryValue = (value: string | number | undefined | null): string => {
   if (value === undefined || value === null || value === '') {
      return '–';
   }

   if (typeof value === 'number') {
      return thousandSeparator(Math.round(value));
   }

   const numericValue = Number(value);
   if (!Number.isNaN(numericValue) && value.trim() !== '') {
      return thousandSeparator(Math.round(numericValue));
   }

   return value;
};

export const DuplicatePartsModal = () => {
   const { visible, duplicates, closeModal } = useDuplicatePartsModalState();
   const [dismissedKeys, setDismissedKeys] = useState<Set<string>>(new Set());
   const [selections, setSelections] = useState<Selections>({});
   const [inactivateOldParts, setInactivateOldParts] = useState<Record<string, boolean>>({});
   const [isSaving, setIsSaving] = useState(false);
   const mutate = useMutationWithCacheUpdate('Values copied and previous parts inactivated');

   const visibleDuplicates = duplicates.filter((duplicate) => !dismissedKeys.has(duplicate.key));
   const duplicatesByItem = visibleDuplicates.reduce<Record<string, DuplicateWithContext[]>>((groups, duplicate) => {
      if (!groups[duplicate.itemNo]) {
         groups[duplicate.itemNo] = [];
      }
      groups[duplicate.itemNo].push(duplicate);
      return groups;
   }, {});

   const handleClose = () => {
      setDismissedKeys(new Set());
      setSelections({});
      setInactivateOldParts({});
      setIsSaving(false);
      closeModal();
   };

   useEffect(() => {
      if (visible && visibleDuplicates.length === 0) {
         handleClose();
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [visible, visibleDuplicates.length]);

   if (!visible || visibleDuplicates.length === 0) {
      return null;
   }

   const toggleField = (key: string, field: TransferField) => {
      setSelections((previous) => {
         const current = new Set(previous[key] || []);
         if (current.has(field)) {
            current.delete(field);
         } else {
            current.add(field);
         }
         return { ...previous, [key]: current };
      });
   };

   const isFieldSelected = (key: string, field: TransferField): boolean => {
      if (selections[key]) {
         return selections[key].has(field);
      }
      return true;
   };

   const hasFieldDiff = (group: DuplicateWithContext, field: TransferField): boolean => {
      return group.oldPart[field] !== group.newPart[field] && hasValue(group.oldPart[field]);
   };

   const shouldInactivate = (key: string): boolean => {
      return inactivateOldParts[key] !== false;
   };

   const toggleInactivate = (key: string) => {
      setInactivateOldParts((previous) => ({ ...previous, [key]: !(previous[key] !== false) }));
   };

   const hasAnySelection = visibleDuplicates.some(
      (group) =>
         TRANSFER_FIELDS.some((field) => hasFieldDiff(group, field) && isFieldSelected(group.key, field)) || shouldInactivate(group.key)
   );

   const dismiss = (key: string) => {
      setDismissedKeys((previous) => new Set(previous).add(key));
      setSelections((previous) => {
         const next = { ...previous };
         delete next[key];
         return next;
      });
      setInactivateOldParts((previous) => {
         const next = { ...previous };
         delete next[key];
         return next;
      });
   };

   const selectAll = () => {
      const allSelections: Selections = {};
      visibleDuplicates.forEach((group) => {
         const fields = new Set<TransferField>();
         TRANSFER_FIELDS.forEach((field) => {
            if (hasFieldDiff(group, field)) {
               fields.add(field);
            }
         });
         if (fields.size > 0) {
            allSelections[group.key] = fields;
         }
      });
      setSelections(allSelections);
   };

   const clearAll = () => {
      const emptySelections: Selections = {};
      visibleDuplicates.forEach((group) => {
         emptySelections[group.key] = new Set();
      });
      setSelections(emptySelections);
   };

   const handleSave = () => {
      const groupsToProcess = visibleDuplicates.filter(
         (group) =>
            TRANSFER_FIELDS.some((field) => hasFieldDiff(group, field) && isFieldSelected(group.key, field)) || shouldInactivate(group.key)
      );

      if (groupsToProcess.length === 0) {
         return;
      }

      setIsSaving(true);

      const updatedSubItems: Record<string, any>[] = [];

      groupsToProcess.forEach((group) => {
         const selectedFields = TRANSFER_FIELDS.filter((field) => hasFieldDiff(group, field) && isFieldSelected(group.key, field));

         if (selectedFields.length > 0) {
            const updatedNewPart = cloneDeep(group.newPart);
            selectedFields.forEach((field) => {
               updatedNewPart[field] = group.oldPart[field];
            });
            updatedNewPart.newFromPia = false;
            updatedSubItems.push(updatedNewPart);
         }

         if (shouldInactivate(group.key)) {
            const updatedOldPart = cloneDeep(group.oldPart);
            updatedOldPart.active = false;
            updatedSubItems.push(updatedOldPart);
         }
      });

      mutate(updatedSubItems, {
         onSuccess: () => {
            setIsSaving(false);
            handleClose();
         },
         onError: () => {
            setIsSaving(false);
         },
      });
   };

   const renderTransferFieldRow = (group: DuplicateWithContext, field: TransferField, side: 'old' | 'new') => {
      const selected = isFieldSelected(group.key, field);
      const hasDiff = hasFieldDiff(group, field);
      const autoUsePrevious = hasDiff && isZeroValue(group.oldPart[field]);

      if (side === 'new') {
         if (hasDiff) {
            return (
               <SelectableValueRow key={field}>
                  <InfoRow style={{ flex: 1 }}>
                     <InfoLabel>{fieldLabel(field)}</InfoLabel>
                     {selected ? <HintText>{formatValue(group.oldPart[field])}</HintText> : <InfoValue>{formatValue(group.newPart[field])}</InfoValue>}
                  </InfoRow>
                  {!autoUsePrevious ? (
                     <StyledTooltip title={`Keep previous ${fieldLabel(field).toLowerCase()} value`}>
                        <span>
                           <Checkbox
                              id={`${group.key}-${field}`}
                              value={`${group.key}-${field}`}
                              checked={selected}
                              onChange={() => toggleField(group.key, field)}
                              label=""
                           />
                        </span>
                     </StyledTooltip>
                  ) : null}
               </SelectableValueRow>
            );
         }

         return (
            <InfoRow key={field}>
               <InfoLabel>{fieldLabel(field)}</InfoLabel>
               <InfoValue>{formatValue(group.newPart[field])}</InfoValue>
            </InfoRow>
         );
      }

      return (
         <InfoRow key={field}>
            <InfoLabel>{fieldLabel(field)}</InfoLabel>
            <InfoValue>{formatValue(group.oldPart[field])}</InfoValue>
         </InfoRow>
      );
   };

   const renderContextFieldRow = (group: DuplicateWithContext, field: ChangeSummaryField, side: 'old' | 'new') => {
      if (isTransferField(field) || group.oldPart[field] === group.newPart[field]) {
         return null;
      }

      const value = side === 'old' ? group.oldPart[field] : group.newPart[field];

      return (
         <InfoRow key={`${side}-${field}`}>
            <InfoLabel>{CHANGE_SUMMARY_LABELS[field]}</InfoLabel>
            <InfoValue>{formatSummaryValue(value)}</InfoValue>
         </InfoRow>
      );
   };

   const hasTransferDiffs = (group: DuplicateWithContext): boolean => TRANSFER_FIELDS.some((field) => hasFieldDiff(group, field));

   return ReactDOM.createPortal(
      <Modal.Overlay>
         <Modal.Container
            onClick={(event) => event.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-labelledby="duplicate-parts-modal-title"
         >
            <Modal.Header>
               <Modal.Title id="duplicate-parts-modal-title">Review refreshed parts</Modal.Title>
               <IconButton onClick={handleClose} icon={crossIcon} disabled={false} title="Close" />
            </Modal.Header>

            <Modal.Content>
               <IntroHint>
                  The refresh detected {visibleDuplicates.length === 1 ? 'a part' : `${visibleDuplicates.length} parts`} with a new version.
                  Only mass and volume can be copied from the previous version to the new one. Other differences are shown for context, and
                  the previous version will be inactivated unless you choose to keep it active.
               </IntroHint>

               <SpreadRow>
                  <SectionLabel>
                     {visibleDuplicates.length} refreshed {visibleDuplicates.length === 1 ? 'part' : 'parts'}
                  </SectionLabel>
                  <LinkGroup>
                     <LinkButton color="secondary" onClick={selectAll}>
                        Select all
                     </LinkButton>
                     <LinkButton color="secondary" onClick={clearAll}>
                        Clear all
                     </LinkButton>
                  </LinkGroup>
               </SpreadRow>

               {Object.entries(duplicatesByItem).map(([itemNo, itemDuplicates]) => (
                  <div key={itemNo} style={{ marginBottom: '16px' }}>
                     <SpreadRow>
                        <div>
                           <SectionLabel style={{ fontSize: tokens.font.size.lg, fontWeight: tokens.font.weight.bold, letterSpacing: '0.25px' }}>
                              {itemNo}
                           </SectionLabel>
                        </div>
                     </SpreadRow>

                     {itemDuplicates.map((group) => {
                        const identityText = `${group.part}${group.partDetail ? ` / ${group.partDetail}` : ''}${group.newPart.materialGrade ? ` / ${group.newPart.materialGrade}` : ''}`;

                        return (
                           <GroupCard key={group.key}>
                              <SpreadRow>
                                 <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', minWidth: 0, marginBottom: '12px' }}>
                                    <HintText style={{ marginTop: 0 }}>
                                       <TruncatedText title={identityText} style={{ maxWidth: '420px' }}>
                                          {identityText}
                                       </TruncatedText>
                                    </HintText>
                                    {group.refreshMessage ? <HintText style={{ marginTop: 0 }}>Reason: {group.refreshMessage}</HintText> : null}
                                 </div>
                                 <StyledTooltip title="Skip this duplicate part and leave it unchanged when you save.">
                                    <span>
                                       <LinkButton color="secondary" onClick={() => dismiss(group.key)}>
                                          Skip this part
                                       </LinkButton>
                                    </span>
                                 </StyledTooltip>
                              </SpreadRow>

                              <ComparisonGrid>
                                 <RelativePanel>
                                    <SectionLabel>PREVIOUS</SectionLabel>
                                    <PanelCornerAction>
                                       <StyledTooltip
                                          title={
                                             shouldInactivate(group.key)
                                                ? 'Previous part will be inactivated — click to keep it active'
                                                : 'Previous part will stay active — click to inactivate it'
                                          }
                                       >
                                          <span>
                                             <Button
                                                ssrIcon={shouldInactivate(group.key) ? inactivateIcon : activateIcon}
                                                iconOnly={true}
                                                small={true}
                                                type={shouldInactivate(group.key) ? 'tertiary' : 'secondary'}
                                                onClick={() => toggleInactivate(group.key)}
                                             />
                                          </span>
                                       </StyledTooltip>
                                    </PanelCornerAction>
                                    {getChangedFields(group).length === 0 ? (
                                       <InfoRow>
                                          <InfoLabel>Changes</InfoLabel>
                                          <InfoValue>No value changes detected</InfoValue>
                                       </InfoRow>
                                    ) : null}
                                    {TRANSFER_FIELDS.map((field) => renderTransferFieldRow(group, field, 'old'))}
                                    {getChangedFields(group).map((field) => renderContextFieldRow(group, field, 'old'))}
                                 </RelativePanel>

                                 <ArrowSeparator>→</ArrowSeparator>

                                 <Panel>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '8px' }}>
                                       <SectionLabel style={{ color: theme.colors.state.success, marginBottom: 0 }}>NEW</SectionLabel>
                                       {hasTransferDiffs(group) ? <Caption>Use previous</Caption> : null}
                                    </div>
                                    {getChangedFields(group).length === 0 ? (
                                       <InfoRow>
                                          <InfoLabel>Changes</InfoLabel>
                                          <InfoValue>No value changes detected</InfoValue>
                                       </InfoRow>
                                    ) : null}
                                    {TRANSFER_FIELDS.map((field) => renderTransferFieldRow(group, field, 'new'))}
                                    {getChangedFields(group).map((field) => renderContextFieldRow(group, field, 'new'))}
                                 </Panel>
                              </ComparisonGrid>

                           </GroupCard>
                        );
                     })}
                  </div>
               ))}
            </Modal.Content>

            <Modal.Footer>
               <Button text="Close" type="tertiary" small={true} onClick={handleClose} />
               <Button
                  text={isSaving ? 'Saving...' : 'Save'}
                  type="emphasised"
                  small={true}
                  disabled={!hasAnySelection || isSaving}
                  onClick={handleSave}
               />
            </Modal.Footer>
         </Modal.Container>
      </Modal.Overlay>,
      document.body
   );
};
